import { Stack, Text, Title } from "@mantine/core"
import {
  CommunicationStudioClasses,
  CUSTOMISATION_CATEGORIES,
} from "@tfg/types/constants/communication-studio.constant"
import type { AGECResponses } from "@tfg/types/endpoints/agec"
import { TextTransform } from "@tfg/types/schemas/communication-studio/cssStylesSchema"
import {
  contentVerticalSpacingCSSVariable,
  headerFontFamilyCSSVariable,
  headerFontSizeCSSVariable,
  headerFontWeightCSSVariable,
  headerLineHeightCSSVariable,
  textColorCssVariable,
  textFontSizeCSSVariable,
  textFontWeightCSSVariable,
  titleColorCSSVariable,
} from "@tfg/types/schemas/communication-studio/cssVariables"

import { useGetStylingParametersContext } from "../context/stylingParameters/useGetStylingParametersContext"
import { useGetTranslationsContext } from "../context/translationsContext/useGetTranslationsContext"
import { DesignStudioParametersToolkit } from "../utils/designStudioParametersToolkit"

interface Props {
  agecData: AGECResponses.GenericAgecData
}

export const PackagingInfo = ({ agecData }: Props) => {
  const { t } = useGetTranslationsContext()
  const { stylingParameters } = useGetStylingParametersContext()
  if (!stylingParameters) return null
  const hasPrimaryPackagingRecycledPercentage =
    agecData.primaryPackagingRecycledPercentage !== null &&
    agecData.primaryPackagingRecycledPercentage > 0
  return (
    <>
      {agecData.withPrimaryPackaging &&
        hasPrimaryPackagingRecycledPercentage && (
          <Stack
            className={CommunicationStudioClasses.PACKAGING_INFO_HEADER}
            gap={DesignStudioParametersToolkit.getCssVariableLiteralName(
              contentVerticalSpacingCSSVariable
            )}
          >
            <Title
              order={3}
              className={CommunicationStudioClasses.PACKAGING_INFO_HEADER}
              c={DesignStudioParametersToolkit.getCssVariableLiteralName(
                titleColorCSSVariable
              )}
              fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
                headerFontWeightCSSVariable
              )}
              fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
                headerFontSizeCSSVariable
              )}
              lh={DesignStudioParametersToolkit.getCssVariableLiteralName(
                headerLineHeightCSSVariable
              )}
              tt={
                DesignStudioParametersToolkit.extractValueFromCssVariable(
                  stylingParameters,
                  CUSTOMISATION_CATEGORIES.GLOBAL,
                  "headerTextTransform"
                ) as TextTransform | undefined
              }
              ff={`${DesignStudioParametersToolkit.getCssVariableLiteralName(
                headerFontFamilyCSSVariable
              )}, ${DesignStudioParametersToolkit.extractFontFamily(stylingParameters)}
           `}
            >
              {t("packaging.title")}
            </Title>
            <Stack gap={0}>
              {hasPrimaryPackagingRecycledPercentage && (
                <Text
                  c={DesignStudioParametersToolkit.getCssVariableLiteralName(
                    textColorCssVariable
                  )}
                  fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
                    textFontWeightCSSVariable
                  )}
                  fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
                    textFontSizeCSSVariable
                  )}
                  style={DesignStudioParametersToolkit.spreadStylingParameters({
                    category: CUSTOMISATION_CATEGORIES.GLOBAL,
                    stylingParameters,
                    stylingParamKeys: ["letterSpacing", "lineHeight"],
                  })}
                >
                  {agecData.primaryPackagingRecycledPercentage === 100
                    ? t("packaging.recycled_100")
                    : t("packaging.recycled", {
                        percent:
                          agecData.primaryPackagingRecycledPercentage?.toString() ??
                          "",
                      })}
                </Text>
              )}
              {agecData.primaryPackagingRecyclable && (
                <Text
                  c={DesignStudioParametersToolkit.getCssVariableLiteralName(
                    textColorCssVariable
                  )}
                  fw={DesignStudioParametersToolkit.getCssVariableLiteralName(
                    textFontWeightCSSVariable
                  )}
                  fz={DesignStudioParametersToolkit.getCssVariableLiteralName(
                    textFontSizeCSSVariable
                  )}
                >
                  {t("packaging.recyclable")}
                </Text>
              )}
            </Stack>
          </Stack>
        )}
    </>
  )
}
